import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCashflow } from '../../context/CashflowContext';
import {
  LayoutGrid,
  Receipt,
  Users,
  Settings2,
  LogOut,
  TrendingUp,
  ChevronDown,
  Plus,
  X,
  FolderOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';

const BRAND_NAME = "Cashflow";

export const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const { cashflows, activeCashflow, switchCashflow } = useCashflow();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutGrid, label: 'Dashboard' },
    { to: '/expenses', icon: Receipt, label: 'Expenses' },
    { to: '/categories', icon: FolderOpen, label: 'Categories' },
    { to: '/team', icon: Users, label: 'Team' },
    { to: '/profile', icon: Settings2, label: 'Profile' },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside className={`sidebar ${isOpen ? 'mobile-open' : ''} z-50`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-stone-200">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-[#021d49] rounded-[4px] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" strokeWidth={1.5} />
              </div>
              <span className="text-xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>{BRAND_NAME}</span>
            </div>
            <button 
              className="lg:hidden p-1 hover:bg-stone-100 rounded"
              onClick={onClose}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cashflow Selector */}
          <div className="px-4 py-4 border-b border-stone-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between px-3 py-2.5 bg-stone-50 hover:bg-stone-100 rounded-[4px] text-left transition-colors"
                  data-testid="cashflow-selector"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Cashflow</p>
                    <p className="text-sm font-medium text-stone-900 truncate">
                      {activeCashflow?.name || 'Select Cashflow'}
                    </p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-stone-400 flex-shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {cashflows.map((cf) => (
                  <DropdownMenuItem
                    key={cf.id}
                    onClick={() => switchCashflow(cf)}
                    className={activeCashflow?.id === cf.id ? 'bg-stone-100' : ''}
                    data-testid={`cashflow-option-${cf.id}`}
                  >
                    {cf.name}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/cashflows/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Cashflow
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={onClose}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" strokeWidth={1.5} />
                <span>{item.label}</span>
              </NavLink>
            ))}
            
            {/* Add Expense Button */}
            <Button
              onClick={() => {
                navigate('/expenses?action=add');
                onClose();
              }}
              className="w-full mt-4 bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px] justify-start"
              data-testid="sidebar-add-expense-btn"
            >
              <Plus className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Add Expense
            </Button>
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-stone-200">
            <div className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center text-stone-600 font-medium text-sm">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-stone-900 truncate">{user?.name}</p>
                <p className="text-xs text-stone-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full mt-2 justify-start text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-[4px]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Sign out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};
